<template>
  <CustomModal ref="modal" :title="isEdit ? 'Modifier l\'article' : 'Nouvel article'" class="article-modal">
    <div class="container">
      <CustomForm ref="form" :inputs=inputs :submit=buttons.submit @submit="preventNativeSubmit">
        <CustomTextField :content=inputs.name />
        <div class="fields-row">
          <CustomTextField :content=inputs.reference />
          <CustomSelectField :content=inputs.category />
        </div>
        <p class="hint">Valeurs habituelles : elles préremplissent une ligne d'achat, sans lier les achats existants.</p>
        <div class="fields-row three">
          <CustomSelectField :content=inputs.unit />
          <CustomTextField :content=inputs.unitPriceHt />
          <CustomSelectField :content=inputs.vatRate />
        </div>
        <CustomTextField :content=inputs.notes />
        <div class="button-container">
          <CustomButton class="button" @click="submitForm" :params=buttons.submit />
          <CustomButton class="button" :is-secondary="true" @click="closeModal()" :params=buttons.cancel />
        </div>
      </CustomForm>
    </div>
  </CustomModal>
</template>

<script>
import CustomButton from "@/components/forms/customs/customButton/CustomButton.vue";
import CustomTextField from "@/components/forms/customs/customTextField/CustomTextField.vue";
import CustomSelectField from "@/components/forms/customs/customSelectField/CustomSelectField.vue";
import CustomForm from "@/components/forms/CustomForm.vue";
import CustomModal from "@/components/modals/CustomModal.vue";
import {NotificationService} from "@vues/v-common";
import {ArticleController} from "@/purchase/controllers/ArticleController";
import {PURCHASE_CATEGORY_ENUM, enumToSelectValues, idOf, unitSelectValues, vatRateSelectValues} from "@/purchase/utils";

const NONE = {label: '—', value: ''};

// Modale d'un article du catalogue : création avec openModal(), modification avec openModal(article) (l'article a un « id » ou un « articleId »).
// callback est appelé après l'enregistrement, avec l'identifiant de l'article.
export default {
  name: 'ArticleModal',
  props: ['callback'],
  components: {CustomModal, CustomForm, CustomSelectField, CustomTextField, CustomButton},
  data() {
    return {
      articleId: undefined,
      inputs: {
        name: {label: 'Nom', value: '', type: 'text', required: true, placeholder: 'Ex : Planche chêne 27 mm'},
        reference: {label: 'Référence', value: '', type: 'text'},
        category: {label: 'Catégorie d\'achat', value: '', type: 'select', selectValues: [{label: 'Aucune', value: ''}, ...enumToSelectValues(PURCHASE_CATEGORY_ENUM)]},
        unit: {label: 'Unité', value: '', type: 'select', selectValues: [NONE, ...unitSelectValues()]},
        unitPriceHt: {label: 'Prix unitaire HT (€)', value: '', type: 'number', step: 'any'},
        vatRate: {label: 'TVA', value: '', type: 'select', selectValues: [NONE, ...vatRateSelectValues()]},
        notes: {label: 'Notes', value: '', type: 'text'},
      },
      buttons: {
        submit: {text: "Enregistrer"},
        cancel: {text: "Annuler"},
      }
    };
  },
  computed: {
    isEdit() {
      return this.articleId !== undefined;
    }
  },
  methods: {
    resetInputs() {
      Object.values(this.inputs).forEach(input => {
        input.value = '';
        input.error = false;
      });
      this.inputs.unit.selectValues = [NONE, ...unitSelectValues()];
      this.inputs.vatRate.selectValues = [NONE, ...vatRateSelectValues()];
    },
    async openModal(article) {
      this.articleId = undefined;
      this.resetInputs();
      this.$refs.modal.openModal();

      if (!article) return;

      this.articleId = article.articleId ?? idOf(article);
      const full = await ArticleController.get(this.articleId);
      if (!full || full.errors) {
        this.closeModal(true);
        return;
      }

      this.inputs.name.value = full.name ?? '';
      this.inputs.reference.value = full.reference ?? '';
      this.inputs.category.value = full.category ?? '';
      // L'unité et le taux de l'article, même s'ils ne font pas partie de ceux proposés
      this.inputs.unit.selectValues = [NONE, ...unitSelectValues(full.unit)];
      this.inputs.unit.value = full.unit ?? '';
      this.inputs.unitPriceHt.value = full.unitPriceHt ?? '';
      this.inputs.vatRate.selectValues = [NONE, ...vatRateSelectValues(full.vatRate)];
      this.inputs.vatRate.value = full.vatRate ?? '';
      this.inputs.notes.value = full.notes ?? '';
    },
    closeModal(event = false) {
      this.$refs.modal?.closeModal(event);
    },
    preventNativeSubmit(event) {
      event?.preventDefault?.();
    },
    text(input) {
      return String(input.value ?? '').trim();
    },
    buildBody() {
      const price = this.text(this.inputs.unitPriceHt).replace(',', '.');
      return {
        name: this.text(this.inputs.name),
        reference: this.text(this.inputs.reference),
        category: this.inputs.category.value || null,
        unit: this.inputs.unit.value || null,
        unitPriceHt: price === '' ? null : Number(price),
        vatRate: this.inputs.vatRate.value === '' ? null : Number(this.inputs.vatRate.value),
        notes: this.text(this.inputs.notes),
      };
    },
    async submitForm(event) {
      this.preventNativeSubmit(event);

      if (!this.text(this.inputs.name)) {
        this.inputs.name.error = true;
        (new NotificationService).addNotification("danger", "Le nom n'est pas renseigné");
        return;
      }

      const body = this.buildBody();
      const res = await this.$refs.form.submitForm(() => this.isEdit
          ? ArticleController.patch(this.articleId, body)
          : ArticleController.post(body));
      if (!res.errors) {
        (new NotificationService).addNotification("success", this.isEdit ? "Article modifié" : "Article créé");
        this.closeModal(true);
        if (this.callback) this.callback(this.isEdit ? this.articleId : res.id);
      }
    }
  }
}
</script>

<style lang="stylus" scoped>
@require '~/src/common/style/common.styl';

.container
  display flex
  flex-direction column

  form
    display flex
    flex-direction column
    gap 4px

    .fields-row
      display grid
      grid-template-columns 1fr 1fr
      gap 12px

      &.three
        grid-template-columns 1fr 1.4fr 1fr

    .hint
      margin 6px 0 0
      font-size 12.5px
      color #8a93a8

    // Le bouton principal est en premier dans le DOM (action par défaut de la touche Entrée),
    // l'ordre visuel est inversé : [Annuler] [Enregistrer]
    .button-container
      display flex
      flex-direction row-reverse
      justify-content flex-start
      gap 10px
      margin-top 12px

      .button
        min-width 110px

@media (max-width 480px)
  .container form .fields-row, .container form .fields-row.three
    grid-template-columns 1fr
</style>

<style lang="stylus">
.article-modal
  > .modal
    width 560px !important
    max-width 100%
    height auto !important
    max-height 92vh
    overflow-y auto
</style>
